import * as THREE from 'three';

const INTERACT_RANGE = 3.8;
const LOOK_DOT_MIN = 0.84;

const BEACON_RADIUS = 0.07;
const BEACON_HEIGHT = 13;
const BEAM_PURPLE = 0xb44dff;
const BEAM_BLUE = 0x44aaff;
const BEACON_OPACITY_BASE = 0.24;
const BEACON_PULSE_SPEED = 2.8;
const BEACON_PULSE_AMP = 0.1;

function createStripeTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  const stripe = 8;
  for (let x = 0; x < canvas.width; x += stripe * 2) {
    ctx.fillStyle = '#9b59b6';
    ctx.fillRect(x, 0, stripe, canvas.height);
    ctx.fillStyle = '#3498db';
    ctx.fillRect(x + stripe, 0, stripe, canvas.height);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 5);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Rare llama pinata — one per match at a POI, [E] to break for mats + consumables. */
export class FpsLlama {
  private readonly group: THREE.Group;
  private readonly bodyParts: THREE.Mesh[] = [];
  private readonly stripeTexture: THREE.CanvasTexture;
  private readonly stripeMaterial: THREE.MeshStandardMaterial;
  private opened = false;
  private beaconMesh: THREE.Mesh | null = null;
  private beaconGeometry: THREE.CylinderGeometry | null = null;
  private beaconMaterial: THREE.MeshBasicMaterial | null = null;
  private bobPhase = Math.random() * Math.PI * 2;

  constructor(sceneRoot: THREE.Group, landX: number, landZ: number) {
    this.stripeTexture = createStripeTexture();
    this.stripeMaterial = new THREE.MeshStandardMaterial({
      map: this.stripeTexture,
      emissive: 0x4a2088,
      emissiveIntensity: 0.35,
      metalness: 0.08,
      roughness: 0.62,
    });

    this.group = new THREE.Group();
    this.group.position.set(landX, 0, landZ);

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.75, 0.38), this.stripeMaterial);
    body.position.y = 0.95;
    body.castShadow = true;
    body.receiveShadow = true;
    this.bodyParts.push(body);

    const neck = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.32, 0.26), this.stripeMaterial);
    neck.position.set(0, 1.38, 0.08);
    neck.castShadow = true;
    this.bodyParts.push(neck);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.3, 0.32), this.stripeMaterial);
    head.position.set(0, 1.58, 0.14);
    head.castShadow = true;
    this.bodyParts.push(head);

    for (const side of [-1, 1] as const) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.42, 0.12), this.stripeMaterial);
      leg.position.set(side * 0.16, 0.42, 0);
      leg.castShadow = true;
      this.bodyParts.push(leg);
    }

    for (const mesh of this.bodyParts) {
      this.group.add(mesh);
    }

    this.addLootBeam();
    sceneRoot.add(this.group);
  }

  private addLootBeam(): void {
    this.beaconGeometry = new THREE.CylinderGeometry(BEACON_RADIUS, BEACON_RADIUS, BEACON_HEIGHT, 16);
    this.beaconMaterial = new THREE.MeshBasicMaterial({
      color: BEAM_PURPLE,
      transparent: true,
      opacity: BEACON_OPACITY_BASE,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.beaconMesh = new THREE.Mesh(this.beaconGeometry, this.beaconMaterial);
    this.beaconMesh.position.set(0, BEACON_HEIGHT * 0.5 + 0.2, 0);
    this.group.add(this.beaconMesh);
  }

  update(dt: number): void {
    if (this.opened) return;
    if (!Number.isFinite(dt) || dt <= 0) dt = 0;

    this.bobPhase += dt * 1.6;
    this.group.position.y = Math.sin(this.bobPhase) * 0.04;

    if (this.beaconMaterial) {
      const t = performance.now() * 0.001;
      this.beaconMaterial.opacity =
        BEACON_OPACITY_BASE + Math.sin(t * BEACON_PULSE_SPEED) * BEACON_PULSE_AMP;
      this.beaconMaterial.color.setHex(Math.sin(t * 1.4) > 0 ? BEAM_PURPLE : BEAM_BLUE);
    }
  }

  shouldShowPrompt(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.opened) return false;
    const center = this.group.position.clone();
    center.y += 1.1;
    const toLlama = center.sub(playerPos);
    const dist = toLlama.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;
    const dir = toLlama.multiplyScalar(1 / dist);
    const look = cameraDir.clone().normalize();
    return look.dot(dir) > LOOK_DOT_MIN;
  }

  tryBreak(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.opened) return false;

    const center = this.group.position.clone();
    center.y += 1.1;
    const toLlama = center.sub(playerPos);
    const dist = toLlama.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;

    const dir = toLlama.multiplyScalar(1 / dist);
    const look = cameraDir.clone().normalize();
    if (look.dot(dir) <= LOOK_DOT_MIN) return false;

    this.opened = true;
    for (const mesh of this.bodyParts) {
      mesh.visible = false;
    }
    if (this.beaconMesh) this.beaconMesh.visible = false;
    return true;
  }

  getOpened(): boolean {
    return this.opened;
  }

  getXZ(): { x: number; z: number } {
    return { x: this.group.position.x, z: this.group.position.z };
  }

  dispose(): void {
    if (this.beaconMesh) {
      this.group.remove(this.beaconMesh);
      this.beaconMesh = null;
    }
    if (this.beaconGeometry) {
      this.beaconGeometry.dispose();
      this.beaconGeometry = null;
    }
    if (this.beaconMaterial) {
      this.beaconMaterial.dispose();
      this.beaconMaterial = null;
    }
    for (const mesh of this.bodyParts) {
      mesh.geometry.dispose();
    }
    this.stripeMaterial.dispose();
    this.stripeTexture.dispose();
    this.group.removeFromParent();
  }
}
